// Tempo maximo que o front espera o POST /comandos. Cobre o pior caso do
// backend: ate 3s esperando o lock + comando via API (25s de timeout, sem
// retry). Para MQTT o backend termina bem antes (~9s no pior caso); esse teto
// existe so para o popup nao ficar preso se o backend/rede travar.
export const COMANDO_REQUEST_TIMEOUT_MS = 32000;

/**
 * Converte um erro do axios (POST /comandos) em um "resultado" no mesmo
 * formato das falhas devolvidas pelo backend ({ verificado: false, motivo }),
 * para o popup tratar tudo por um unico caminho.
 *
 * Motivos: limite_requisicoes, operacao_em_andamento (409), erro_requisicao
 * (outros 4xx), erro_servidor (5xx), timeout_cliente, erro_rede.
 */
export const classificarErroComando = (err) => {
  if (err?.isRateLimitError) {
    return { verificado: false, motivo: 'limite_requisicoes', retryAfter: err.retryAfter };
  }

  const status = err?.response?.status;

  if (status === 409 && err.response.data?.dados?.code === 'command_in_progress') {
    return {
      verificado: false,
      motivo: 'operacao_em_andamento',
      retryAfterMs: err.response.data.dados.retryAfterMs
    };
  }

  if (status >= 500) {
    return { verificado: false, motivo: 'erro_servidor' };
  }

  if (status >= 400) {
    return {
      verificado: false,
      motivo: 'erro_requisicao',
      detalhe: err.response.data?.mensagem
    };
  }

  if (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT') {
    return { verificado: false, motivo: 'timeout_cliente' };
  }

  return { verificado: false, motivo: 'erro_rede' };
};

// Classe de cada motivo = o que o operador pode fazer a seguir:
// - falha: esgotou sem confirmacao; seguro assumir que nada mudou -> tentar de novo
// - incerto: pode ter sido aplicado, sem prova -> conferir o status antes de reenviar
// - bloqueado: o servidor nem aceitou o comando -> aguardar e tentar de novo
// Textos para o operador (porteiro): sempre dizem a proxima acao, sem jargao.
// O codigo tecnico (motivo) fica so no resultado/logs.
const TEXTO_SEM_COMUNICACAO = 'Não foi possível falar com o exaustor. Tente de novo. Se continuar, entre em contato com o suporte.';
const TEXTO_SEM_CONFIRMACAO = 'Não conseguimos confirmar se o comando funcionou. Confira o status do exaustor na tela.';

const MOTIVOS = {
  timeout: { classe: 'falha', texto: 'O exaustor não respondeu. Tente de novo. Se continuar, entre em contato com o suporte.' },
  resposta_invalida: { classe: 'falha', texto: TEXTO_SEM_COMUNICACAO },
  mqtt_error: { classe: 'falha', texto: TEXTO_SEM_COMUNICACAO },
  api_error: { classe: 'falha', texto: TEXTO_SEM_COMUNICACAO },
  tipo_desconhecido: { classe: 'falha', texto: TEXTO_SEM_COMUNICACAO },
  erro_interno: { classe: 'falha', texto: TEXTO_SEM_COMUNICACAO },
  erro_rede: { classe: 'falha', titulo: 'Sem conexão', texto: 'Verifique a internet e tente de novo.' },
  erro_requisicao: { classe: 'falha', texto: 'Não foi possível enviar o comando. Tente de novo.' },
  verificacao_divergente: { classe: 'incerto', texto: TEXTO_SEM_CONFIRMACAO },
  erro_servidor: { classe: 'incerto', texto: TEXTO_SEM_CONFIRMACAO },
  timeout_cliente: { classe: 'incerto', texto: TEXTO_SEM_CONFIRMACAO },
  operacao_em_andamento: { classe: 'bloqueado', texto: 'Outra pessoa está usando este módulo. Tente de novo em alguns segundos.' },
  limite_requisicoes: { classe: 'bloqueado', texto: 'Muitos comandos seguidos. Aguarde um instante.' }
};

/**
 * Descreve uma falha de comando para o operador.
 * @returns {{ classe: 'falha'|'incerto'|'bloqueado', titulo?: string, texto: string }}
 */
export const descreverFalhaComando = (resultado) => {
  const motivo = MOTIVOS[resultado?.motivo] || MOTIVOS.erro_requisicao;
  // 4xx: a mensagem do backend ja e voltada ao usuario (ex.: exaustor nao encontrado)
  if (resultado?.motivo === 'erro_requisicao' && resultado.detalhe) {
    return { ...motivo, texto: resultado.detalhe };
  }
  return motivo;
};

// 409: retryAfterMs e o pior caso do lock (via API passa de 75s), entao tem
// teto; se o modulo ainda estiver ocupado, o 409 volta e a contagem recomeca.
const ESPERA_PADRAO_S = 10;
const ESPERA_MAX_LOCK_S = 30;

/** Segundos ate o "Tentar de novo" liberar (0 = sem espera). */
export const segundosAteNovaTentativa = (resultado) => {
  if (resultado?.motivo === 'operacao_em_andamento') {
    const segundos = resultado.retryAfterMs ? Math.ceil(resultado.retryAfterMs / 1000) : ESPERA_PADRAO_S;
    return Math.min(segundos, ESPERA_MAX_LOCK_S);
  }
  if (resultado?.motivo === 'limite_requisicoes') {
    // retryAfter (s) vem do interceptor do axios: fim da janela do rate limit
    return Number(resultado.retryAfter) || ESPERA_PADRAO_S;
  }
  return 0;
};
